import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

function useInView(ref: React.RefObject<HTMLElement | null>) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

const MILESTONES = [
  { year: '1999', title: 'Founded', desc: 'Started as a small software consultancy with a vision to transform businesses through technology.' },
  { year: '2005', title: 'First Major Enterprise Client', desc: 'Delivered our first large-scale enterprise resource planning system for a Fortune 500 company.' },
  { year: '2010', title: 'Product Suite Launch', desc: 'Launched our flagship Inventory Management and Ship Chandling Quotation systems.' },
  { year: '2015', title: 'ISO 9001:2015 Certified', desc: 'Achieved ISO 9001:2015 certification, reaffirming our commitment to quality management.' },
  { year: '2018', title: 'AI Division Established', desc: 'Launched dedicated AI & Machine Learning division, developing cutting-edge intelligent solutions.' },
  { year: '2022', title: 'ISO/IEC 27001:2022 Certified', desc: 'Attained ISO/IEC 27001:2022 information security certification, ensuring world-class data protection.' },
  { year: '2024', title: '25 Years of Excellence', desc: 'Celebrating 25 years of innovation with 500+ delivered projects and 98% client satisfaction rate.' },
];

const VALUES = [
  { icon: 'ri-award-line', title: 'Quality First', desc: 'ISO 9001:2015 certified quality management processes ensure every solution meets the highest standards.' },
  { icon: 'ri-shield-check-line', title: 'Security Guaranteed', desc: 'ISO/IEC 27001:2022 certified — your data and systems are protected by internationally recognized security protocols.' },
  { icon: 'ri-lightbulb-line', title: 'Innovation Driven', desc: 'We constantly explore emerging technologies to deliver cutting-edge solutions that keep you ahead of the competition.' },
  { icon: 'ri-handshake-line', title: 'Client Partnership', desc: 'We build long-term relationships, not just software. Your success is the measure of our success.' },
];

const TEAM = [
  {
    name: 'James Richardson', role: 'CEO & Founder', yrs: '25 years experience',
    img: 'https://readdy.ai/api/search-image?query=professional%20male%20CEO%20founder%20executive%20headshot%20portrait%20confident%20smile%20business%20suit%20white%20background%20clean%20corporate%20photo%2050s%20experienced%20leader&width=300&height=300&seq=team-jr&orientation=squarish',
  },
  {
    name: 'Lisa Nguyen', role: 'CTO & AI Lead', yrs: '18 years experience',
    img: 'https://readdy.ai/api/search-image?query=professional%20asian%20female%20CTO%20technology%20leader%20headshot%20portrait%20confident%20smile%20business%20casual%20attire%20white%20background%20clean%20corporate%20photo%20tech%20executive&width=300&height=300&seq=team-ln&orientation=squarish',
  },
  {
    name: 'Omar Farooq', role: 'Head of Solutions', yrs: '20 years experience',
    img: 'https://readdy.ai/api/search-image?query=professional%20south%20asian%20male%20solutions%20architect%20head%20headshot%20portrait%20confident%20smile%20business%20suit%20white%20background%20clean%20corporate%20photo%20experienced&width=300&height=300&seq=team-of&orientation=squarish',
  },
  {
    name: 'Maria Santos', role: 'Head of Design', yrs: '15 years experience',
    img: 'https://readdy.ai/api/search-image?query=professional%20latina%20female%20creative%20design%20head%20headshot%20portrait%20confident%20smile%20creative%20business%20casual%20attire%20white%20background%20clean%20corporate%20photo%20design%20leader&width=300&height=300&seq=team-ms&orientation=squarish',
  },
];

export default function AboutPage() {
  const valRef = useRef<HTMLElement>(null);
  const valInView = useInView(valRef);
  const teamRef = useRef<HTMLElement>(null);
  const teamInView = useInView(teamRef);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero Banner */}
        <section className="relative pt-32 pb-20 overflow-hidden bg-white">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-teal-50/80 blur-3xl -translate-y-1/4 translate-x-1/4" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
            <div className="max-w-3xl">
              {/* <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-5">
                About Us
              </span> */}
              <h1 className="text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-6">
                25 Years of Delivering <span className="text-teal-500">IT Excellence</span>
              </h1>
              <p className="text-gray-500 text-xl leading-relaxed mb-8 max-w-2xl">
                Since 1999, we&apos;ve been empowering businesses across the globe with intelligent software solutions — combining deep technical expertise with a passion for innovation.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-3 bg-teal-50 border border-teal-200 rounded-xl px-5 py-3">
                  <i className="ri-award-fill text-teal-600 text-xl" />
                  <div>
                    <div className="text-xs text-teal-600 font-semibold">ISO Certified</div>
                    <div className="text-sm font-bold text-gray-900">9001:2015</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-teal-50 border border-teal-200 rounded-xl px-5 py-3">
                  <i className="ri-shield-check-fill text-teal-600 text-xl" />
                  <div>
                    <div className="text-xs text-teal-600 font-semibold">ISO/IEC Certified</div>
                    <div className="text-sm font-bold text-gray-900">27001:2022</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-teal-50 border border-teal-200 rounded-xl px-5 py-3">
                  <i className="ri-time-fill text-teal-600 text-xl" />
                  <div>
                    <div className="text-xs text-teal-600 font-semibold">Since 1999</div>
                    <div className="text-sm font-bold text-gray-900">25+ Years</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-teal-600 py-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { val: '25+', label: 'Years of Experience' },
              { val: '500+', label: 'Projects Delivered' },
              { val: '200+', label: 'Enterprise Clients' },
              { val: '40+', label: 'Expert Engineers' },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-4xl font-extrabold text-white">{s.val}</div>
                <div className="text-teal-200 text-sm mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
            <div>
              {/* <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-5">
                Our Mission
              </span> */}
              <h2 className="text-4xl font-extrabold text-gray-900 mb-5">
                Empowering Businesses Through Intelligent Technology
              </h2>
              <p className="text-gray-500 text-base leading-relaxed mb-5">
                Our mission is to deliver innovative, reliable, and secure IT solutions that help businesses of all sizes thrive in a rapidly evolving digital landscape. We combine 25 years of technical expertise with a deep understanding of business challenges to create software that genuinely makes a difference.
              </p>
              <p className="text-gray-500 text-base leading-relaxed mb-8">
                Guided by our ISO 9001:2015 quality management certification and ISO/IEC 27001:2022 information security standards, we ensure every solution we deliver meets the highest benchmarks for performance, reliability, and data protection.
              </p>
              <Link to="/contact" className="cursor-pointer whitespace-nowrap inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white font-semibold px-7 py-3.5 rounded-lg transition-colors">
                Partner With Us <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="rounded-2xl overflow-hidden border border-gray-100">
              <div className="w-full h-96">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20technology%20company%20office%20interior%20with%20team%20collaboration%20open%20space%20large%20windows%20natural%20light%20minimalist%20design%20teal%20green%20accents%20professional%20IT%20company%20workspace%20clean%20contemporary%20bright&width=700&height=500&seq=about-office-001&orientation=landscape"
                  alt="Our Office"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ISO Certifications */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-12">
              {/* <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-4">
                Certifications
              </span> */}
              <h2 className="text-3xl font-extrabold text-gray-900">Internationally Certified Standards</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-7">
              <div className="bg-white border border-teal-200 rounded-2xl p-8 flex gap-6">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-600 text-white rounded-2xl flex-shrink-0">
                  <i className="ri-award-fill text-3xl" />
                </div>
                <div>
                  <div className="text-xs font-bold text-teal-600 uppercase tracking-widest mb-1">Quality Management</div>
                  <h3 className="text-2xl font-extrabold text-gray-900 mb-2">ISO 9001:2015</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    Our ISO 9001:2015 certification demonstrates our commitment to consistent quality management. Every project follows structured processes for requirement analysis, development, testing, and delivery — ensuring predictable, high-quality outcomes for every client.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {['Quality Assurance', 'Process Management', 'Continuous Improvement', 'Client Focus'].map((b) => (
                      <span key={b} className="bg-teal-50 text-teal-700 text-xs font-medium px-3 py-1 rounded-full border border-teal-200">{b}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="bg-white border border-teal-200 rounded-2xl p-8 flex gap-6">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-600 text-white rounded-2xl flex-shrink-0">
                  <i className="ri-shield-check-fill text-3xl" />
                </div>
                <div>
                  <div className="text-xs font-bold text-teal-600 uppercase tracking-widest mb-1">Information Security</div>
                  <h3 className="text-2xl font-extrabold text-gray-900 mb-2">ISO/IEC 27001:2022</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    Our ISO/IEC 27001:2022 certification establishes that we manage information security risks to the highest international standards. Client data, source code, and intellectual property are protected by comprehensive security controls and regular audits.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {['Data Protection', 'Risk Management', 'Security Audits', 'Compliance'].map((b) => (
                      <span key={b} className="bg-teal-50 text-teal-700 text-xs font-medium px-3 py-1 rounded-full border border-teal-200">{b}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Values */}
        <section ref={valRef} className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-12">
              {/* <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-4">
                Our Values
              </span> */}
              <h2 className="text-3xl font-extrabold text-gray-900">What Drives Us Every Day</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {VALUES.map((v, i) => (
                <div
                  key={v.title}
                  className={`bg-gray-50 border border-gray-100 rounded-2xl p-7 hover:border-teal-200 transition-all duration-500 ${valInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-100 text-teal-600 rounded-xl mb-5">
                    <i className={`${v.icon} text-2xl`} />
                  </div>
                  <h3 className="text-gray-900 font-bold mb-3">{v.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Leadership Team */}
        <section ref={teamRef} className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-12">
              {/* <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-4">
                Our Team
              </span> */}
              <h2 className="text-3xl font-extrabold text-gray-900">Leadership Team</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-7">
              {TEAM.map((member, i) => (
                <div
                  key={member.name}
                  className={`bg-gray-50 border border-gray-100 rounded-2xl overflow-hidden hover:border-teal-200 transition-all duration-500 ${teamInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <div className="w-full h-56 overflow-hidden">
                    <img src={member.img} alt={member.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="p-5">
                    <h4 className="font-bold text-gray-900">{member.name}</h4>
                    <div className="text-teal-600 text-sm font-medium mt-0.5">{member.role}</div>
                    <div className="text-gray-400 text-xs mt-1">{member.yrs}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-teal-600">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-extrabold text-white mb-4">Ready to Start Your Project?</h2>
            <p className="text-teal-100 text-base mb-8">25 years of expertise, ISO-certified quality, and a team dedicated to your success.</p>
            <Link to="/contact" className="cursor-pointer whitespace-nowrap bg-white hover:bg-gray-50 text-teal-700 font-semibold px-8 py-4 rounded-xl transition-colors inline-flex items-center gap-2">
              <i className="ri-calendar-check-line" /> Get Free Consultation
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
